import type {
  Connection,
  ConnectionContact,
  ConnectionCreate,
  MatterIncidentReport,
  SourceOwnedConnectionCreate,
} from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import { getConnectionPreviewContact } from "./item-contact-connections";

export const INCIDENT_REPORT_CONTACT_CONNECTION_TYPES = {
  "Incident Report Source": "matter_incident_report_to_incident_report_source",
  Other: "matter_incident_report_to_other",
  "Police Department": "matter_incident_report_to_police_department",
  "Primary Contact": "matter_incident_report_to_primary_contact",
  Witness: "matter_incident_report_to_witness",
} as const;

export type SupportedIncidentReportContactRole =
  keyof typeof INCIDENT_REPORT_CONTACT_CONNECTION_TYPES;

const getConnectionTypes = (role: SupportedIncidentReportContactRole) => {
  const nativeType = INCIDENT_REPORT_CONTACT_CONNECTION_TYPES[role];
  return [
    nativeType,
    nativeType.replace(
      "matter_incident_report_to_",
      "matter_incident_report_to_legacy_",
    ),
  ];
};

export const getIncidentReportContactConnections = (
  report: MatterIncidentReport | undefined,
  role: SupportedIncidentReportContactRole,
): Connection[] => {
  if (!report) return [];

  return report.connections.filter(
    (connection) =>
      connection.sourceObjectType === "matter_incident_report" &&
      connection.sourceObjectId === report.id &&
      connection.targetObjectType === "contact" &&
      connection.state === "active" &&
      getConnectionTypes(role).includes(connection.connectionType),
  );
};

export const getIncidentReportConnectionContactName = (
  contact: ConnectionContact | null | undefined,
) => (contact ? formatContactsName(contact) : undefined) ?? "Unknown contact";

export const getIncidentReportConnectionFormContact = (
  connection: Connection,
) => {
  if (!connection.contact) return undefined;

  return {
    contactName: getIncidentReportConnectionContactName(connection.contact),
    id: connection.contact.id,
    type: connection.contact.type,
  };
};

export const getIncidentReportConnectionPreviewContact =
  getConnectionPreviewContact;

export const createIncidentReportContactConnectionInput = (
  incidentReportId: string,
  contactId: string,
  role: SupportedIncidentReportContactRole,
): ConnectionCreate => ({
  connectionType: INCIDENT_REPORT_CONTACT_CONNECTION_TYPES[role],
  operationId: crypto.randomUUID(),
  sourceObjectId: incidentReportId,
  sourceObjectType: "matter_incident_report",
  targetObjectId: contactId,
  targetObjectType: "contact",
});

export const createIncidentReportContactConnectionChildInput = (
  contactId: string,
  role: SupportedIncidentReportContactRole,
): SourceOwnedConnectionCreate => ({
  connectionType: INCIDENT_REPORT_CONTACT_CONNECTION_TYPES[role],
  targetObjectId: contactId,
  targetObjectType: "contact",
});
